import { create } from 'zustand';
import type { LoginResponse, MeResponse, MembershipView, UserView } from '@shared/api/types';

export type SessionStatus = 'booting' | 'authenticated' | 'anonymous';

export interface ImpersonationInfo {
  /** Display name of the person being viewed as. */
  subjectName: string;
  /** Display name of the staff member doing the viewing. */
  actorName: string;
  startedAt: string;
}

interface SessionState {
  status: SessionStatus;
  /** JWT access token — memory only, never persisted (security doc, brief D17). */
  accessToken: string | null;
  user: UserView | null;
  /** Enriched /users/me payload (active roles, solo mode, permissions). */
  me: MeResponse | null;
  memberships: MembershipView[];
  activeOrganizationId: string | null;
  mfaEnrollmentRequired: boolean;
  impersonation: ImpersonationInfo | null;

  applyLogin: (response: LoginResponse) => void;
  applyMe: (me: MeResponse) => void;
  setAccessToken: (token: string) => void;
  setActiveOrganization: (organizationId: string | null) => void;
  setMfaEnrollmentRequired: (required: boolean) => void;
  setImpersonation: (info: ImpersonationInfo | null) => void;
  markAnonymous: () => void;
  clear: () => void;
}

const initial = {
  accessToken: null,
  user: null,
  me: null,
  memberships: [],
  activeOrganizationId: null,
  mfaEnrollmentRequired: false,
  impersonation: null,
} satisfies Partial<SessionState>;

export const useSession = create<SessionState>((set) => ({
  status: 'booting',
  ...initial,
  applyLogin: (response) =>
    set({
      status: 'authenticated',
      accessToken: response.access_token,
      user: response.user,
      memberships: response.memberships,
      activeOrganizationId: response.active_organization_id,
      mfaEnrollmentRequired: response.mfa_enrollment_required,
    }),
  applyMe: (me) =>
    set((state) => ({
      me,
      user: me,
      memberships: me.memberships,
      activeOrganizationId: me.active_organization_id ?? state.activeOrganizationId,
      mfaEnrollmentRequired: state.mfaEnrollmentRequired && !me.mfa_enabled,
    })),
  setAccessToken: (accessToken) => set({ accessToken }),
  setActiveOrganization: (activeOrganizationId) => set({ activeOrganizationId }),
  setMfaEnrollmentRequired: (mfaEnrollmentRequired) => set({ mfaEnrollmentRequired }),
  setImpersonation: (impersonation) => set({ impersonation }),
  markAnonymous: () => set({ status: 'anonymous', ...initial }),
  clear: () => set({ status: 'anonymous', ...initial }),
}));

/** Non-React accessor for the API client and router guards. */
export const sessionStore = {
  get: () => useSession.getState(),
  subscribe: useSession.subscribe,
};
